import { Button } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useHeroToast, usePermissions, useWarmup } from 'src/hooks';
import { UpdateOrganizationAccountManagerRequest } from 'src/types';

import {
  ContentLoadError,
  DataTable,
  ModalForm,
  PageTitle,
  PageWrapper,
  Spinner,
  WithErrorBoundary,
} from '../../../../components';
import {
  useGetOrganizationCollectionEndpoint,
  usePatchOrgAccountManagerDocumentFirestore,
  useQueryHelper,
} from '../../../../queries';
import type { ResponseDataProps } from '../../../../queries/organizationsQueryFirebase/useGetOrganizationCollectionEndpoint';
import { CreateAcountManagerForm } from '../../components/CreateAcountManagerForm';
import { OrganizationColumns } from './columns';

const PAGE_SIZE = 10;

const normalize = (value: unknown): string => (typeof value === 'string' ? value.toLowerCase() : '');

const matchesSearch = (org: ResponseDataProps, needle: string): boolean => {
  if (!needle) return true;
  const haystacks = [
    normalize(org.name),
    normalize(org.hotel),
    normalize(org.companyName),
    normalize(org.companyEmail),
    normalize(org.companyPhone),
    normalize(org.city),
    normalize(org.companyCity),
    normalize(org.code),
    normalize(org.webAppUrl),
    normalize(org.plan),
  ];
  return haystacks.some((h) => h.includes(needle));
};

export const OrganizationsList: FC = () => {
  const navigate = useNavigate();
  useWarmup([
    'v3-getOrganizationsCollection',
    'v3-setOrganizationStatus',
    'v3-deleteOrganization',
    'v3-updateOrgAccountManager',
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') ?? '1');
  const searchTerm = searchParams.get('q') ?? '';

  const [showAccountManagerModal, setShowAccountManagerModal] = useState<boolean>(false);
  const [currentOrgId, setCurrentOrgId] = useState<string>('');
  const [currentAccountManager, setCurrentAccountManager] = useState<string>('');

  const orgDocument = useGetOrganizationCollectionEndpoint();
  const { data: org, refetch, isLoading } = orgDocument;
  const { isError, errorMessage } = useQueryHelper({
    queries: [orgDocument],
  });

  const queryClient = useQueryClient();
  const { mutate, isPending } = usePatchOrgAccountManagerDocumentFirestore();

  const { showPromise } = useHeroToast();
  // `onlyAttraction` = podgląd organizacji bez akcji (edycja, POI, usuwanie...).
  const { isOnlyAttraction } = usePermissions();

  const { pageData, totalPages } = useMemo(() => {
    const all = Array.isArray(org) ? org : [];
    const needle = searchTerm.trim().toLowerCase();
    const filtered = needle ? all.filter((o) => matchesSearch(o, needle)) : all;
    const total = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(Math.max(1, currentPage), total);
    const start = (safePage - 1) * PAGE_SIZE;
    return {
      pageData: filtered.slice(start, start + PAGE_SIZE),
      totalPages: total,
    };
  }, [org, searchTerm, currentPage]);

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(page));
      return next;
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set('q', value);
      } else {
        next.delete('q');
      }
      next.set('page', '1');
      return next;
    });
  };

  const openManagerModal = (id: string, accountManager?: string) => {
    setCurrentOrgId(id);
    setCurrentAccountManager(accountManager || '');
    setShowAccountManagerModal(true);
  };

  const handleSubmit = (data: UpdateOrganizationAccountManagerRequest): void => {
    showPromise(
      () => new Promise(
        (resolve, reject) => {
          mutate(
            { id: currentOrgId, data },
            {
              onSuccess: resolve,
              onError: reject,
            },
          );
        },
      ),
      <Spinner />,
    ).then(() => {
      navigate('/organizations');
      queryClient.resetQueries({ queryKey: ['organizations'] });
      setShowAccountManagerModal(false);
    });
  };

  if (isError) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={errorMessage} />
      </PageWrapper>
    );
  }

  return (
    <WithErrorBoundary>
      <PageWrapper>
        <PageTitle
          title="Organizations"
          actions={(
            <div className="flex items-center gap-2">
              {!isOnlyAttraction && (
                <Button
                  size="sm"
                  color="secondary"
                  radius="sm"
                  onPress={() => navigate('/organizations/new')}
                >
                  Add new
                </Button>
              )}
            </div>
          )}
        />
        <DataTable
          data={pageData}
          columns={OrganizationColumns(openManagerModal, isOnlyAttraction)}
          isLoading={isLoading}
          isError={false}
          pageSize={PAGE_SIZE}
          getRowClassName={(row) => (row.isDisabled ? 'opacity-60 bg-warning/5' : '')}
          searchPlaceholder="Search by hotel, name, email, city..."
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          searchValue={searchTerm}
          onSearchChange={handleSearchChange}
        />
        <ModalForm
          isLoading={isPending}
          form="edit-account-manager-form"
          title="Account Manager"
          isOpen={showAccountManagerModal}
          onClose={() => setShowAccountManagerModal(false)}
        >
          <CreateAcountManagerForm
            onSubmit={(e: UpdateOrganizationAccountManagerRequest) => handleSubmit(e)}
            initialValue={{ account_manager: currentAccountManager }}
          />
        </ModalForm>
      </PageWrapper>
    </WithErrorBoundary>
  );
};
